/**
 * Full-viewport ambient motion layers.
 * Active layer selected via data-bg-fx (themes/bgFx.ts). Colors follow skin tokens.
 */

import { useEffect, useState } from "react";
import { getBgFxId, type BgFxId } from "../themes/bgFx";

function useBgFxId(): BgFxId {
  const [id, setId] = useState<BgFxId>(() => getBgFxId());
  useEffect(() => {
    const sync = () => setId(getBgFxId());
    window.addEventListener("voiceforge-bg-fx", sync);
    const obs = new MutationObserver(() => {
      const attr = document.documentElement.getAttribute("data-bg-fx") as BgFxId | null;
      if (attr) setId(attr);
    });
    obs.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-bg-fx"],
    });
    return () => {
      window.removeEventListener("voiceforge-bg-fx", sync);
      obs.disconnect();
    };
  }, []);
  return id;
}

const STAR_SEEDS = Array.from({ length: 42 }, (_, i) => ({
  left: ((i * 47) % 97) + 1.5,
  top: ((i * 31) % 93) + 2,
  delay: (i % 12) * 0.35,
  dur: 2.2 + (i % 7) * 0.45,
  size: 1 + (i % 3),
}));

const SPARK_SEEDS = Array.from({ length: 32 }, (_, i) => ({
  left: (i * 53) % 100,
  delay: (i % 10) * 0.4,
  dur: 4 + (i % 6) * 0.7,
  hue: i % 3,
}));

const RAIN_SEEDS = Array.from({ length: 26 }, (_, i) => ({
  left: (i * 37) % 100,
  delay: (i % 14) * 0.25,
  dur: 1.4 + (i % 5) * 0.35,
  len: 28 + (i % 6) * 10,
  hue: i % 3,
}));

const HEX_SEEDS = Array.from({ length: 12 }, (_, i) => ({
  left: ((i * 17) % 90) + 5,
  top: ((i * 23) % 80) + 8,
  delay: (i % 8) * 0.5,
  scale: 0.55 + (i % 5) * 0.18,
}));

const FLAME_SEEDS = Array.from({ length: 24 }, (_, i) => ({
  left: 15 + ((i * 29) % 70),
  delay: (i % 8) * 0.35,
  dur: 2.2 + (i % 5) * 0.4,
  size: 4 + (i % 4) * 2,
  hue: i % 2,
}));

export function BackgroundFx() {
  const fx = useBgFxId();

  return (
    <div className="pf-bg-fx" aria-hidden>
      {/* Orbs */}
      <div className="pf-fx-layer pf-fx-orbs" data-active={fx === "orbs" || undefined}>
        <div className="pf-orb pf-orb-a" />
        <div className="pf-orb pf-orb-b" />
        <div className="pf-orb pf-orb-c" />
      </div>

      {/* Mesh */}
      <div className="pf-fx-layer pf-fx-mesh" data-active={fx === "mesh" || undefined}>
        <div className="pf-mesh-sheet" />
      </div>

      {/* Aurora */}
      <div className="pf-fx-layer pf-fx-aurora" data-active={fx === "aurora" || undefined}>
        <div className="pf-aurora-band pf-aurora-1" />
        <div className="pf-aurora-band pf-aurora-2" />
        <div className="pf-aurora-band pf-aurora-3" />
      </div>

      {/* Stars */}
      <div className="pf-fx-layer pf-fx-stars" data-active={fx === "stars" || undefined}>
        <div className="pf-star-drift" />
        {STAR_SEEDS.map((s, i) => (
          <span
            key={i}
            className="pf-star"
            style={{
              left: `${s.left}%`,
              top: `${s.top}%`,
              width: s.size,
              height: s.size,
              animationDelay: `${s.delay}s`,
              animationDuration: `${s.dur}s`,
            }}
          />
        ))}
      </div>

      {/* Sparkles */}
      <div className="pf-fx-layer pf-fx-sparkles" data-active={fx === "sparkles" || undefined}>
        {SPARK_SEEDS.map((s, i) => (
          <span
            key={i}
            className={`pf-spark-rise pf-spark-h${s.hue}`}
            style={{
              left: `${s.left}%`,
              animationDelay: `${s.delay}s`,
              animationDuration: `${s.dur}s`,
            }}
          />
        ))}
      </div>

      {/* Cyber grid */}
      <div className="pf-fx-layer pf-fx-grid" data-active={fx === "grid" || undefined}>
        <div className="pf-grid-floor" />
        <div className="pf-grid-scan" />
      </div>

      {/* Ribbons */}
      <div className="pf-fx-layer pf-fx-ribbons" data-active={fx === "ribbons" || undefined}>
        <div className="pf-ribbon pf-ribbon-1" />
        <div className="pf-ribbon pf-ribbon-2" />
        <div className="pf-ribbon pf-ribbon-3" />
      </div>

      {/* Hex */}
      <div className="pf-fx-layer pf-fx-hex" data-active={fx === "hex" || undefined}>
        {HEX_SEEDS.map((s, i) => (
          <span
            key={i}
            className="pf-hex"
            style={{
              left: `${s.left}%`,
              top: `${s.top}%`,
              transform: `scale(${s.scale})`,
              animationDelay: `${s.delay}s`,
            }}
          />
        ))}
      </div>

      {/* Rain */}
      <div className="pf-fx-layer pf-fx-rain" data-active={fx === "rain" || undefined}>
        {RAIN_SEEDS.map((s, i) => (
          <span
            key={i}
            className={`pf-raindrop pf-spark-h${s.hue}`}
            style={{
              left: `${s.left}%`,
              height: s.len,
              animationDelay: `${s.delay}s`,
              animationDuration: `${s.dur}s`,
            }}
          />
        ))}
      </div>

      {/* Flame / embers */}
      <div className="pf-fx-layer pf-fx-flame" data-active={fx === "flame" || undefined}>
        {FLAME_SEEDS.map((s, i) => (
          <span
            key={i}
            className={`pf-ember pf-ember-h${s.hue}`}
            style={{
              left: `${s.left}%`,
              width: s.size,
              height: s.size,
              animationDelay: `${s.delay}s`,
              animationDuration: `${s.dur}s`,
            }}
          />
        ))}
        <div className="pf-flame-glow" />
      </div>

      {/* Radar scan */}
      <div className="pf-fx-layer pf-fx-scan" data-active={fx === "scan" || undefined}>
        <div className="pf-radar-sweep" />
        <div className="pf-radar-ring pf-radar-ring-1" />
        <div className="pf-radar-ring pf-radar-ring-2" />
      </div>
    </div>
  );
}
